import { useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, PackagePlus, Plus, Search } from "lucide-react";
import { fmtQty, type GoodsReceiptRecord } from "@/types/inventory";
import { ReceiveDialog } from "@/components/inventory/ReceiveDialog";
import { ReceiptDetailDialog } from "@/components/inventory/ReceiptDetailDialog";

const day = (d: string) => new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });

const FILTERS = [
  { key: "RECEIVED", label: "Received" },
  { key: "CANCELLED", label: "Cancelled" },
  { key: "ALL", label: "All" },
];

export function GoodsReceiptsPage() {
  const { error } = useToast();
  const { hasPermission } = useAuth();
  const canReceive = hasPermission("inventory.receive");
  const [search, setSearch] = useUrlSearch();
  const [filter, setFilter] = useState("RECEIVED");
  const [receive, setReceive] = useState<"PURCHASE" | "OPENING" | null>(null);
  const [detail, setDetail] = useState<string | null>(null);

  const list = usePagedList<GoodsReceiptRecord>({
    deps: [search, filter],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      if (filter !== "ALL") qs.set("status", filter);
      try {
        return unwrapPage<GoodsReceiptRecord>(await api.get(`/api/GoodsReceipt/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load receipts");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link to="/inventory" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="size-3" /> Inventory
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Goods receipts</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Material received from suppliers, and opening stock entries</p>
        </div>
        {canReceive && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setReceive("OPENING")}>
              <Plus className="size-4" />
              Opening stock
            </Button>
            <Button size="sm" className="h-9 gap-1.5 shadow-sm" onClick={() => setReceive("PURCHASE")}>
              <PackagePlus className="size-4" />
              Receive goods
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by GRN, supplier or invoice…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={5} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Receipt</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Material</TableHead>
                <TableHead className="text-right">Rolls</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((g) => (
                <TableRow key={g.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setDetail(g.id)}>
                  <TableCell>
                    <code className="font-mono text-sm font-semibold">{g.grn_number}</code>
                    <div className="text-[11px] text-muted-foreground">
                      {day(g.receipt_date)} · {g.warehouse?.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {g.receipt_type === "OPENING" ? <Badge variant="secondary">Opening stock</Badge> : g.supplier?.name || "-"}
                    {g.supplier_invoice_no && <div className="text-[11px] text-muted-foreground">Inv {g.supplier_invoice_no}</div>}
                  </TableCell>
                  <TableCell className="text-sm max-w-[320px]">
                    {g.lines.slice(0, 2).map((l) => (
                      <div key={l.id} className="truncate">
                        {l.product?.name} <span className="text-muted-foreground">· {fmtQty(l.quantity, l.unit)}</span>
                      </div>
                    ))}
                    {g.lines.length > 2 && <div className="text-[11px] text-muted-foreground">+{g.lines.length - 2} more</div>}
                  </TableCell>
                  <TableCell className="text-right text-sm">{g.unit_count}</TableCell>
                  <TableCell>{g.receipt_status === "CANCELLED" ? <Badge variant="outline">Cancelled</Badge> : <Badge variant="success">Received</Badge>}</TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
                    No receipts here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <ReceiveDialog open={!!receive} mode={receive || "PURCHASE"} onClose={() => setReceive(null)} onSaved={list.reload} />
      <ReceiptDetailDialog receiptId={detail} onClose={() => setDetail(null)} onChanged={list.reload} />
    </div>
  );
}
